// Single source of truth for the visual system.
// Use these tokens instead of inlining colour/font/shadow values in components.

// ── Colour ──────────────────────────────────────────────────────────────────
export const COLORS = {
  // Text on dark glass backgrounds
  textPrimary: 'rgba(255,255,255,0.96)',
  textHigh:    'rgba(255,255,255,0.92)',
  textBody:    'rgba(255,255,255,0.66)',
  textMuted:   'rgba(255,255,255,0.46)',
  textFine:    'rgba(255,255,255,0.40)',

  // Brand
  pink:     '#F36BAF',
  purple:   '#8B7CFF',
  gradient: 'linear-gradient(135deg, #F36BAF 0%, #8B7CFF 100%)',

  // Pastel-on-dark status pair
  ok:        '#9DEFD6',
  okBg:      'rgba(157,239,214,0.14)',
  okBord:    'rgba(157,239,214,0.32)',
  replace:   '#FFB6E0',
  replaceBg: 'rgba(255,182,224,0.14)',
  replaceBord: 'rgba(255,182,224,0.32)',
  missing:   '#C5B6FF',
  missingBg: 'rgba(197,182,255,0.14)',
  missingBord: 'rgba(197,182,255,0.36)',

  // Glass surfaces
  glassBg:        'rgba(255,255,255,0.10)',
  glassBgStrong:  'rgba(255,255,255,0.16)',
  glassBgSubtle:  'rgba(255,255,255,0.06)',
  glassBorder:    'rgba(255,255,255,0.20)',
  glassBorderHi:  'rgba(255,255,255,0.32)',
  glassRow:       'rgba(255,255,255,0.05)',
  glassRowBorder: 'rgba(255,255,255,0.10)',

  // Per-section pastel accents (icon badge backgrounds)
  tintPink:   'rgba(243,107,175,0.18)',
  tintPurple: 'rgba(139,124,255,0.20)',
  tintBlue:   'rgba(158,216,255,0.20)',
  tintMint:   'rgba(183,240,223,0.20)',

  // Subtle glow colours that match the brand for hover states
  glowPink:   'rgba(243,107,175,0.25)',
  glowPurple: 'rgba(139,124,255,0.25)',
  glowBlue:   'rgba(158,216,255,0.25)',
  glowMint:   'rgba(183,240,223,0.25)',
};

// ── Typography ──────────────────────────────────────────────────────────────
// Sizes use CSS string values so callers can drop them into inline styles
// directly. Hero is intentionally largest; section H2 is the standard.
export const TYPE = {
  heroH1:     'clamp(34px, 4.2vw, 58px)',
  pageH1:     'clamp(28px, 3.6vw, 40px)',
  sectionH2:  'clamp(26px, 3.2vw, 46px)',
  sectionSub: 'clamp(14px, 1.6vw, 17px)',
  cardTitle:  '15px',
  cardText:   '13.5px',
  body:       '14px',
  meta:       '13px',
  hint:       '12.5px',
  fineprint:  '12px',
  eyebrow:    '11px',
};

export const WEIGHT = {
  bold:     800,
  semibold: 700,
  medium:   600,
  regular:  400,
};

export const LETTER = {
  tightTitle: '-0.022em',
  tight:      '-0.01em',
  uppercase:  '0.10em',
  uppercaseWide: '0.13em',
};

// ── Shadows ─────────────────────────────────────────────────────────────────
export const SHADOW = {
  glass:      '0 8px 32px rgba(20,10,40,0.18), 0 1px 0 rgba(255,255,255,0.10) inset',
  glassHover: '0 16px 48px rgba(20,10,40,0.28), 0 1px 0 rgba(255,255,255,0.16) inset',
  ctaPink:
    '0 8px 28px rgba(243,107,175,0.40), 0 0 36px rgba(243,107,175,0.30), 0 0 64px rgba(139,124,255,0.18)',
  ctaPinkHover:
    '0 14px 46px rgba(243,107,175,0.62), 0 0 56px rgba(243,107,175,0.52), 0 0 88px rgba(139,124,255,0.30)',
};

// ── Geometry ────────────────────────────────────────────────────────────────
export const RADIUS = {
  card: 22,
  pill: 100,
  btn:  14,
  iconBadge: 14,
};

export const SPACE = {
  // Section vertical padding, common pattern
  sectionY: 'clamp(48px, 6vw, 80px)',
  // Section horizontal padding
  sectionX: 'clamp(24px, 5vw, 80px)',
  // Card grid gap
  gridGap:  'clamp(12px, 2vw, 20px)',
};

// ── Backdrop blur strengths ─────────────────────────────────────────────────
export const BLUR = {
  card:    'blur(20px)',
  cardLg:  'blur(24px)',
  overlay: 'blur(14px)',
};

// ── Manrope font stack (single declaration, mirrors body in index.css) ──────
export const FONT = {
  family: "'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};
